import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Toolbar } from "primereact/toolbar";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { useAuthStore } from "../../../store/AuthStore";
import { useSchoolStore } from "../../../store/SchoolStore";
import FacultyAddEdit from "../FacultyAddEdit/Index";
import {
  deleteFaculties,
  deleteFaculty,
  getFaculties,
} from "../../../apis/Faculty";
import utils from "../../../utils";
import constants from "../../../constants";
import DepartmentList from "../DepartmentList/Index";

const FacultyList = ({ toast }) => {
  const [state] = useAuthStore();
  const [{ schoolId }] = useSchoolStore();

  let emptyFaculty = {
    id: null,
    schoolId: schoolId,
    name: "",
    description: "",
    createdBy: state.email,
    modifiedBy: state.email,
  };

  const [faculties, setFaculties] = useState(null);
  const [facultyDialog, setFacultyDialog] = useState(false);
  const [deleteFacultyDialog, setDeleteFacultyDialog] = useState(false);
  const [deleteFacultiesDialog, setDeleteFacultiesDialog] = useState(false);
  const [faculty, setFaculty] = useState(emptyFaculty);
  const [selectedFaculties, setSelectedFaculties] = useState(null);
  const [, setSubmitted] = useState(false);
  const [globalFilter, setGlobalFilter] = useState(null);
  const toastFL = useRef(toast);
  const dt = useRef(null);
  const [expandedRows, setExpandedRows] = useState(null);

  async function fetchFacultiesData() {
    var res = await getFaculties(schoolId);
    setFaculties(res.data);
  }

  useEffect(() => {
    fetchFacultiesData();
  }, [facultyDialog, deleteFacultyDialog, deleteFacultiesDialog, globalFilter]);

  const openNew = () => {
    setFaculty(emptyFaculty);
    setSubmitted(false);
    setFacultyDialog(true);
  };

  const confirmDeleteSelected = () => {
    setDeleteFacultiesDialog(true);
  };

  const leftToolbarTemplate = () => {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          label="New"
          icon="pi pi-plus"
          severity="success"
          onClick={openNew}
        />
        <Button
          label="Delete"
          icon="pi pi-trash"
          severity="danger"
          onClick={confirmDeleteSelected}
          disabled={!selectedFaculties || !selectedFaculties.length}
        />
      </div>
    );
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const rightToolbarTemplate = () => {
    return (
      <Button
        label="Export"
        icon="pi pi-upload"
        className="p-button-help"
        onClick={exportCSV}
      />
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <Button
          icon="pi pi-pencil"
          rounded
          outlined
          className="mr-2"
          onClick={() => editFaculty(rowData)}
        />
        <Button
          icon="pi pi-trash"
          rounded
          outlined
          severity="danger"
          onClick={() => confirmDeleteFaculty(rowData)}
        />
      </React.Fragment>
    );
  };

  const editFaculty = (facultyData) => {
    setFaculty({ ...facultyData });
    setFacultyDialog(true);
  };

  const confirmDeleteFaculty = (facultyData) => {
    setFaculty(facultyData);
    setDeleteFacultyDialog(true);
  };

  const hideDeleteFacultyDialog = () => {
    setDeleteFacultyDialog(false);
  };

  const hideDeleteFacultiesDialog = () => {
    setDeleteFacultiesDialog(false);
  };

  const deleteFacultyFunc = async () => {
    let resp = {
      data: null,
      responseCode: "",
      responseCodeDescription: "",
    };

    try {
      if (faculty && faculty.id) {
        resp = await deleteFaculty(faculty.id);
        setDeleteFacultyDialog(false);
        setFaculty(emptyFaculty);
        toastFL.current.show(
          utils.toastCallback({
            severity: "success",
            summary: "Successful",
            detail: resp.responseCodeDescription,
          })
        );
      }
    } catch (error) {
      if (error.response) {
        resp = error.response;

        toast.current.show(
          utils.toastCallback({
            severity: "error",
            summary: "Submit Error",
            detail: resp.responseCodeDescription,
          })
        );
      } else {
        toast.current.show(
          utils.toastCallback({
            severity: "error",
            summary: "Network Error",
            detail: constants.networkErrorMessage,
          })
        );
      }
    }
  };

  const deleteSelectedFaculties = async () => {
    let resp = {
      data: null,
      responseCode: "",
      responseCodeDescription: "",
    };

    try {
      let _faculties = faculties.filter(
        (val) => !selectedFaculties.includes(val)
      );

      resp = await deleteFaculties(_faculties);
      setDeleteFacultiesDialog(false);
      setSelectedFaculties(null);
      toastFL.current.show(
        utils.toastCallback({
          severity: "success",
          summary: "Successful",
          detail: resp.responseCodeDescription,
        })
      );
    } catch (error) {
      if (error.response) {
        resp = error.response;

        toast.current.show(
          utils.toastCallback({
            severity: "error",
            summary: "Submit Error",
            detail: resp.responseCodeDescription,
          })
        );
      } else {
        toast.current.show(
          utils.toastCallback({
            severity: "error",
            summary: "Network Error",
            detail: constants.networkErrorMessage,
          })
        );
      }
    }
  };

  const deleteFacultyDialogFooter = (
    <React.Fragment>
      <Button
        label="No"
        icon="pi pi-times"
        outlined
        onClick={hideDeleteFacultyDialog}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        severity="danger"
        onClick={async () => await deleteFacultyFunc()}
      />
    </React.Fragment>
  );

  const deleteFacultiesDialogFooter = (
    <React.Fragment>
      <Button
        label="No"
        icon="pi pi-times"
        outlined
        onClick={hideDeleteFacultiesDialog}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        severity="danger"
        onClick={deleteSelectedFaculties}
      />
    </React.Fragment>
  );

  const allowExpansion = (rowData) => {
    return (rowData && rowData != null && Array.isArray(rowData) && rowData.length > 0) 
    ? rowData.departmentCount.length > 0 
      : false;

  };

  const collapseAll = () => {
    setExpandedRows(null);
  };

  const expandAll = () => {

    let _expandedRows = {};

    if(faculties && faculties != null && Array.isArray(faculties) && faculties.length > 0) faculties.forEach((p) => (_expandedRows[`${p.id}`] = true));

    setExpandedRows(_expandedRows);
  };

  const header = (
    <div className="flex flex-wrap justify-content-end gap-2">
      <Button
        icon="pi pi-plus"
        label="Expand Departments"
        onClick={expandAll}
        text
      />
      <Button
        icon="pi pi-minus"
        label="Collapse Departments"
        onClick={collapseAll}
        text
      />
    </div>
  );

  const rowExpansionTemplate = (data) => {
    return (
      <div className="p-3">
        <h5>Departments for {data.name}</h5>
          <DepartmentList toast={toastFL}  facultyId={data.id}/> 
      </div> 
      );
};

  const onRowExpand = (event) => {
    toast.current.show({
      severity: "info",
      summary: "Department Expanded",
      detail: event.data.name,
      life: 3000,
    });
  };

  const onRowCollapse = (event) => {
    toast.current.show({
      severity: "success",
      summary: "Department Collapsed",
      detail: event.data.name,
      life: 3000,
    });
  };

  return (
    <div>
      <Toast ref={toastFL} />
      <div className="card">
        <Toolbar
          className="mb-4"
          left={leftToolbarTemplate}
          right={rightToolbarTemplate}
        ></Toolbar>

        <DataTable
          ref={dt}
          value={faculties}
          selection={selectedFaculties}
          onSelectionChange={(e) => setSelectedFaculties(e.value)}
          dataKey="id"
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Faculties"
          globalFilter={globalFilter}
          header={header}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          onRowExpand={onRowExpand}
          onRowCollapse={onRowCollapse}
          rowExpansionTemplate={rowExpansionTemplate}
          tableStyle={{ minWidth: "60rem" }}
        >
          <Column expander={allowExpansion} style={{ width: "5rem" }} />

          <Column selectionMode="multiple" exportable={false}></Column>
          <Column
            field="name"
            header="Name"
            sortable
            style={{ minWidth: "16rem" }}
          ></Column>
          <Column
            field="description"
            header="Description"
            style={{ minWidth: "16rem" }}
          ></Column>
          <Column
            body={actionBodyTemplate}
            exportable={false}
            style={{ minWidth: "12rem" }}
          ></Column>
        </DataTable>
      </div>

      <Dialog
        visible={deleteFacultyDialog}
        style={{ width: "32rem" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={deleteFacultyDialogFooter}
        onHide={hideDeleteFacultyDialog}
      >
        <div className="confirmation-content">
          <i
            className="pi pi-exclamation-triangle mr-3"
            style={{ fontSize: "2rem" }}
          />
          {faculty && (
            <span>
              Are you sure you want to delete <b>{faculty.name}</b>?
            </span>
          )}
        </div>
      </Dialog>

      <Dialog
        visible={deleteFacultiesDialog}
        style={{ width: "32rem" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={deleteFacultiesDialogFooter}
        onHide={hideDeleteFacultiesDialog}
      >
        <div className="confirmation-content">
          <i
            className="pi pi-exclamation-triangle mr-3"
            style={{ fontSize: "2rem" }}
          />
          {faculty && (
            <span>Are you sure you want to delete the selected Faculties?</span>
          )}
        </div>
      </Dialog>

      <FacultyAddEdit
        facultyAE={faculty}
        facultyDialog={facultyDialog}
        setFacultyDialog={setFacultyDialog}
      />
    </div>
  );
};

export default FacultyList;
